/**
 * Project Bobcat — one use-case agent's steps, in a form that both an agent
 * template and an AI draft can be written in, and that `agent-spec-rows.ts`
 * turns into real table rows.
 *
 * Steps point at each other by `ref`, not by node number: numbers are only
 * known once the rows are placed in a solution. Two targets are special:
 * `return` (back to the Supervisor, for the customer's next question) and
 * `handoff` (to a person, through one shared hand-off step the converter adds
 * when anything uses it).
 *
 * Pure: no React, no store, no model call — imported by the server route that
 * drafts an agent and by the client that applies one.
 */

/** Back to the Supervisor's first node. */
export const RETURN_TARGET = 'return';
/** To a person: the agent's shared hand-off step. */
export const HANDOFF_TARGET = 'handoff';

export interface AgentSpecButton {
  label: string;
  /** A step `ref`, `return` or `handoff`. */
  next: string;
}

export interface AgentSpecOutcome {
  /** The command's result for the decision variable: true, false, error… */
  value: string;
  next: string;
}

/** A step that talks to the customer: a Decision node. */
export interface AgentSpecSay {
  ref: string;
  type: 'D';
  name: string;
  message: string;
  /** The customer answers here (the flow waits). */
  answerRequired?: boolean;
  /** Free text here is an answer, not something to route on. */
  nluDisabled?: boolean;
  /** Where the answer is kept, UPPERCASE_WITH_UNDERSCORES. */
  variables?: string[];
  /** Buttons; each leads somewhere. */
  buttons?: AgentSpecButton[];
  next?: string;
}

/** A step that runs a command: an Action node. */
export interface AgentSpecDo {
  ref: string;
  type: 'A';
  name: string;
  command: string;
  /** Read by the script; usually names the variables it reads and writes. */
  parameterInput?: Record<string, unknown>;
  decisionVariable: string;
  outcomes: AgentSpecOutcome[];
  /** Variables the command sets. */
  variables?: string[];
  /**
   * What the placeholder script answers until the real one is uploaded
   * (`placeholder-scripts.ts`): "false" (not connected) unless a template
   * says otherwise — a validator passes ("true"), an availability check
   * says agents are "online", so the flow still works end to end.
   */
  stub?: string;
}

export type AgentSpecStep = AgentSpecSay | AgentSpecDo;

export interface AgentSpec {
  /** In order; the first is where the Supervisor sends the customer. */
  steps: AgentSpecStep[];
  /**
   * What is left for a person to do before this agent works for real,
   * one short line each ("Upload the OrderLookup script in Actions").
   */
  connect: string[];
}

/** An AI draft is kept short enough to read on one card. */
export const MAX_SPEC_STEPS = 9;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max: number): string =>
  typeof v === 'string' ? v.trim().slice(0, max) : '';
const VARIABLE = /^[A-Z][A-Z0-9_]*$/;
const COMMAND = /^[A-Za-z][A-Za-z0-9_]*$/;
const variablesOf = (v: unknown): string[] =>
  (Array.isArray(v) ? v : [])
    .map((x) => str(x, 64).toUpperCase())
    .filter((x) => VARIABLE.test(x))
    .slice(0, 6);

/**
 * A spec as a model returned it, checked: unknown fields dropped, every
 * target pointing at a real step (or `return` / `handoff`), the first step a
 * Decision node. Returns null when nothing usable is left.
 */
export const readAgentSpec = (
  raw: unknown,
  { maxSteps = MAX_SPEC_STEPS }: { maxSteps?: number } = {}
): AgentSpec | null => {
  if (!isRecord(raw) || !Array.isArray(raw.steps)) return null;

  const steps: AgentSpecStep[] = [];
  const seen = new Set<string>();
  raw.steps.slice(0, maxSteps).forEach((s, i) => {
    if (!isRecord(s)) return;
    const ref = str(s.ref, 40) || `s${i + 1}`;
    if (seen.has(ref) || ref === RETURN_TARGET || ref === HANDOFF_TARGET) {
      return;
    }
    const name = str(s.name, 80);
    if (!name) return;
    if (s.type === 'A') {
      const command = str(s.command, 80);
      const decisionVariable = str(s.decisionVariable, 40) || 'success';
      const outcomes = (Array.isArray(s.outcomes) ? s.outcomes : [])
        .filter(isRecord)
        .map((o) => ({
          value: str(o.value, 30).toLowerCase(),
          next: str(o.next, 40),
        }))
        .filter((o) => o.value && o.next)
        .slice(0, 6);
      if (!COMMAND.test(command) || !outcomes.length) return;
      seen.add(ref);
      steps.push({
        ref,
        type: 'A',
        name,
        command,
        ...(isRecord(s.parameterInput)
          ? { parameterInput: s.parameterInput }
          : {}),
        decisionVariable,
        outcomes,
        variables: variablesOf(s.variables),
        ...(str(s.stub, 30) ? { stub: str(s.stub, 30).toLowerCase() } : {}),
      });
      return;
    }
    const message = str(s.message, 600);
    if (!message) return;
    const buttons = (Array.isArray(s.buttons) ? s.buttons : [])
      .filter(isRecord)
      .map((b) => ({ label: str(b.label, 40), next: str(b.next, 40) }))
      .filter((b) => b.label && b.next)
      .slice(0, 5);
    seen.add(ref);
    steps.push({
      ref,
      type: 'D',
      name,
      message,
      ...(s.answerRequired === true ? { answerRequired: true } : {}),
      ...(s.nluDisabled === true ? { nluDisabled: true } : {}),
      variables: variablesOf(s.variables),
      ...(buttons.length ? { buttons } : {}),
      ...(str(s.next, 40) ? { next: str(s.next, 40) } : {}),
    });
  });

  if (!steps.length || steps[0].type !== 'D') return null;

  // Every target resolves: a step, return or handoff. A dangling one becomes
  // a hand-off rather than a line to nowhere.
  const valid = (t: string) =>
    seen.has(t) || t === RETURN_TARGET || t === HANDOFF_TARGET;
  const fix = (t: string) => (valid(t) ? t : HANDOFF_TARGET);
  const fixed = steps.map((step): AgentSpecStep => {
    if (step.type === 'A') {
      return {
        ...step,
        outcomes: step.outcomes.map((o) => ({ ...o, next: fix(o.next) })),
      };
    }
    return {
      ...step,
      ...(step.buttons
        ? { buttons: step.buttons.map((b) => ({ ...b, next: fix(b.next) })) }
        : {}),
      ...(step.next ? { next: fix(step.next) } : {}),
    };
  });

  // One line each; a long one is cut at a word, with an ellipsis.
  const connect = (Array.isArray(raw.connect) ? raw.connect : [])
    .map((c) => (typeof c === 'string' ? c.trim() : ''))
    .filter(Boolean)
    .map((c) =>
      c.length <= 240 ? c : `${c.slice(0, 240).replace(/\s+\S*$/, '')}…`
    )
    .slice(0, 4);

  return { steps: fixed, connect };
};
